import { useQueryDocument } from '@/services/folders/queries'
import { DocumentInterface } from '@/services/folders/types'
import { useQueryCustomTemplates, useQueryTemplates } from '@/services/template/queries'
import { TemplateType } from '@/services/template/types'
import { Editor } from '@tiptap/core'
import { createContext, useEffect, useState } from 'react'
import { useParams } from 'react-router'

/* eslint-disable react-refresh/only-export-components -- context module */

export type FileContextType = {
    file: DocumentInterface
    templateEditor: Editor | null
    setTemplateEditor: (editor: Editor | null) => void
    fileEditor: Editor | null
    setFileEditor: (editor: Editor | null) => void
    outputEditor: Editor | null
    setOutputEditor: (editor: Editor | null) => void
    isLoadingFiles: boolean
    inFileEditor: boolean
    templates: TemplateType[]
    customTemplates: TemplateType[]
    isLoadingTemplates: boolean
    filesInTabs: DocumentInterface[]
    addFileToTabs: (file: DocumentInterface) => void
    removeFileFromTabs: (file: DocumentInterface) => void
    fileInView: DocumentInterface
    setFileInView: (file: DocumentInterface) => void
    openRefreshModal: boolean
    setOpenRefreshModal: (value: boolean) => void
    openAI: boolean
    setOpenAI: (value: boolean) => void
    refreshDocument: () => void
}

const defaultFilesContextValues: FileContextType = {
    file: {} as DocumentInterface,
    templateEditor: null,
    setTemplateEditor: () => undefined,
    fileEditor: null,
    setFileEditor: () => undefined,
    isLoadingFiles: false,
    inFileEditor: false,
    templates: [],
    customTemplates: [],
    isLoadingTemplates: false,
    outputEditor: null,
    setOutputEditor: () => undefined,
    filesInTabs: [],
    addFileToTabs: () => undefined,
    removeFileFromTabs: () => undefined,
    fileInView: {} as DocumentInterface,
    setFileInView: () => undefined,
    openRefreshModal: false,
    setOpenRefreshModal: () => undefined,
    openAI: false,
    setOpenAI: () => undefined,
    refreshDocument: () => undefined,
}

export const FileContext = createContext(defaultFilesContextValues)

export default function FileContextProvider({ children }: { children: React.ReactNode }) {
    const params = useParams<{ file: string }>()
    const [templateEditor, setTemplateEditor] = useState<Editor | null>(null)
    const [fileEditor, setFileEditor] = useState<Editor | null>(null)
    const [outputEditor, setOutputEditor] = useState<Editor | null>(null)
    const [fileInView, setFileInView] = useState<DocumentInterface>({} as DocumentInterface)
    const [filesInTabs, setFilesInTabs] = useState<DocumentInterface[]>([])
    const [openRefreshModal, setOpenRefreshModal] = useState(false)
    const [openAI, setOpenAI] = useState(false)

    const {
        data = {} as DocumentInterface,
        isLoading,
        refetch,
    } = useQueryDocument({
        enabled: !!params.file,
        queryParams: {
            id: params.file as string,
        },
    })

    const { data: templates, isLoading: isTemplatesLoading } = useQueryTemplates({})
    const { data: customTemplates, isLoading: isCustomTemplatesLoading } = useQueryCustomTemplates({})

    const addFileToTabs = (file: DocumentInterface) => {
        const fileExists = filesInTabs.find(f => f.id === file.id)

        if (!fileExists && file.id !== data?.id) {
            setFilesInTabs([...filesInTabs, file])
        }
    }

    const removeFileFromTabs = (file: DocumentInterface) => {
        const newFiles = filesInTabs.filter(f => f.id !== file.id)
        setFilesInTabs(newFiles)
    }

    useEffect(() => {
        setFileInView(data)
        setFilesInTabs([])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname])

    return (
        <FileContext.Provider
            value={{
                file: data as DocumentInterface,
                templateEditor,
                setTemplateEditor,
                fileEditor,
                setFileEditor,
                isLoadingFiles: isLoading && !!params.file,
                inFileEditor: !!params.file,
                templates: templates || [],
                customTemplates: customTemplates || [],
                isLoadingTemplates: isTemplatesLoading || isCustomTemplatesLoading,
                outputEditor,
                setOutputEditor,
                filesInTabs,
                addFileToTabs,
                removeFileFromTabs,
                fileInView: fileInView?.id ? fileInView : (data as DocumentInterface),
                setFileInView,
                openRefreshModal,
                setOpenRefreshModal,
                openAI,
                setOpenAI,
                refreshDocument: refetch,
            }}
        >
            {children}
        </FileContext.Provider>
    )
}
